import moment from "moment/moment";
import { MultimediaTranslation } from "../../../../gen/witbooking/backoffice/common/common_pb";
import { ExtraCategorizationMapper } from "../../../extrasCategories/infrastructure/mappers/ExtraCategorizationMapper";
import { mapExtraCategoryFormToDomain } from "../../../extrasCategories/presentation/mappers/extra-category-form.mapper";
import { Image } from "../../../shared/widgets/ImageManager";
import {
  ExtraApplyTo,
  ExtraLockType,
  ExtraRateType,
  ExtraRestrictionUse,
  ExtraType,
  ExtraUse,
  TranslationType,
  GuestType as GuestTypeEnum,
} from "../../types";
import { FormType } from "../types";
import {
  BookableRef,
  BookableRefInventory,
  BookableRefList,
  DateWindow,
  DateWindowPeriod,
  DateWindowPeriodDateRange,
  Extra,
  GuestType,
  LockType,
  MaxStayDuration,
  MinStayDuration,
  Multimedia,
  Rate,
  RateConstant,
  RestrictionAvailability,
  RestrictionBlockage,
  RestrictionCode,
  RestrictionMaxNotice,
  RestrictionMaxStay,
  RestrictionMinNotice,
  RestrictionMinStay,
  Restrictions,
  RestrictionTravelWindow,
  RestrictionValue,
  RestrictionValueConstant,
  RestrictionValueOwn,
  SimpleDuration,
  StayDaysFilterType,
  Translation,
  Type,
  UnlockCode,
  UnlockCodes,
  Variant,
} from "../../../../gen/witbooking/backoffice/extra/v1/extra_api_pb";

export interface MapToGRPCOptions {
  /** Sends `categorization` (flag on). Absent, the backend keeps the stored values. */
  withCategorization?: boolean;
}

export const mapToGRPCFromObject = (
  data: FormType,
  multimediaList?: Image[],
  { withCategorization = false }: MapToGRPCOptions = {}
): Extra => {
  const extraObj = new Extra();
  extraObj.setId(data.id);
  extraObj.setActive(data.active);
  extraObj.setVisible(data.visible);
  extraObj.setTicker(data.ticker);
  extraObj.setType(mapType(data));
  extraObj.setVariant(mapVariant(data));
  extraObj.setLockType(mapLockType(data));
  extraObj.setStayDaysFilterType(mapStayDaysFilterType(data));
  extraObj.setRestrictions(mapRestrictions(data));
  extraObj.setRate(mapRate(data));
  extraObj.setNotificationEmailsList(mapNotificationEmailsList(data));
  extraObj.setAppliesToList(mapAppliesToInventoryList(data));
  extraObj.setTranslationsList(mapTranslationsList(data));
  extraObj.setMultimediaList(mapMultimediaList(multimediaList));
  extraObj.setAppliesToGuestsList(mapAppliesToGuestsList(data));
  extraObj.setTax(data.tax);

  if (data.maxUnits) {
    extraObj.setMaxBookableUnits(Number(data.maxUnits));
  }

  if (withCategorization) {
    extraObj.setCategorization(
      ExtraCategorizationMapper.toProto(mapExtraCategoryFormToDomain(data))
    );
  }

  return extraObj;
};

export const mapToGRPCBookableRefFromObject = (inventoryList: string[]) => {
  const bookableRefInventory = new BookableRefInventory();
  bookableRefInventory.setTickersList(inventoryList);

  const bookableRef = new BookableRef();
  bookableRef.setInventory(bookableRefInventory);

  const bookableRefList = new BookableRefList();
  bookableRefList.addBookableRefs(bookableRef);

  return bookableRefList;
};

const mapTranslationsList = ({ translationsList }: FormType) => {
  return translationsList.map(({ locale, name, description }: TranslationType) => {
    const result = new Translation();
    result.setLocale(locale);
    result.setName(name);
    result.setDescription(description);
    return result;
  });
};

const mapType = ({ type }: FormType) => {
  switch (type) {
    case ExtraType.EXTRA_TYPE_ROOM:
    case ExtraType.EXTRA_TYPE_ROOM_MANDATORY:
    case ExtraType.EXTRA_TYPE_ROOM_PER_DAY:
    case ExtraType.EXTRA_TYPE_ROOM_PER_DAY_MANDATORY:
    case ExtraType.EXTRA_TYPE_ROOM_SPECIFIC_DATE:
      return Type.ROOM;
    case ExtraType.EXTRA_TYPE_PERSON:
    case ExtraType.EXTRA_TYPE_PERSON_PER_DAY:
    case ExtraType.EXTRA_TYPE_PERSON_PER_DAY_MANDATORY:
    case ExtraType.EXTRA_TYPE_PERSON_MANDATORY:
    case ExtraType.EXTRA_TYPE_PERSON_SPECIFIC_DATE:
      return Type.PERSON;
    case ExtraType.EXTRA_TYPE_UNIT:
    case ExtraType.EXTRA_TYPE_UNIT_PER_DAY:
    case ExtraType.EXTRA_TYPE_UNIT_SPECIFIC_DATE:
      return Type.UNIT;
    default:
      throw new Error(`type [${type}] not supported`);
  }
};

const mapVariant = ({ type }: FormType) => {
  switch (type) {
    case ExtraType.EXTRA_TYPE_ROOM_MANDATORY:
    case ExtraType.EXTRA_TYPE_PERSON_MANDATORY:
      return Variant.MANDATORY;
    case ExtraType.EXTRA_TYPE_ROOM_PER_DAY:
    case ExtraType.EXTRA_TYPE_PERSON_PER_DAY:
    case ExtraType.EXTRA_TYPE_UNIT_PER_DAY:
      return Variant.PER_DAY;
    case ExtraType.EXTRA_TYPE_ROOM_PER_DAY_MANDATORY:
    case ExtraType.EXTRA_TYPE_PERSON_PER_DAY_MANDATORY:
      return Variant.PER_DAY_MANDATORY;
    case ExtraType.EXTRA_TYPE_ROOM_SPECIFIC_DATE:
    case ExtraType.EXTRA_TYPE_PERSON_SPECIFIC_DATE:
    case ExtraType.EXTRA_TYPE_UNIT_SPECIFIC_DATE:
      return Variant.SPECIFIC_DATE;
    case ExtraType.EXTRA_TYPE_ROOM:
    case ExtraType.EXTRA_TYPE_PERSON:
    case ExtraType.EXTRA_TYPE_UNIT:
      return Variant.UNKNOWN;
    default:
      throw new Error(`variant for type [${type}] not supported`);
  }
};

const mapLockType = ({ lockType }: FormType) => {
  switch (lockType) {
    case ExtraLockType.EXTRA_LOCK_TYPE_DAY:
      return LockType.DAY;
    case ExtraLockType.EXTRA_LOCK_TYPE_STAY:
      return LockType.STAY;
    default:
      throw new Error(`lockType [${lockType}] not supported`);
  }
};

const mapStayDaysFilterType = ({ applyTo }: FormType) => {
  switch (applyTo) {
    case ExtraApplyTo.EXTRA_APPLY_TO_ALL_STAY_DAYS_EXCEPT_CHECKOUT:
      return StayDaysFilterType.ALL_STAY_DAYS_EXCEPT_CHECKOUT;
    case ExtraApplyTo.EXTRA_APPLY_TO_ALL_STAY_DAYS_EXCEPT_CHECKIN:
      return StayDaysFilterType.ALL_STAY_DAYS_EXCEPT_CHECKIN;
    case ExtraApplyTo.EXTRA_APPLY_TO_ONLY_CHECKIN:
      return StayDaysFilterType.ONLY_CHECKIN;
    default:
      throw new Error(`applyTo [${applyTo}] not supported`);
  }
};

const mapNotificationEmailsList = ({ emailsList }: FormType) => {
  if (!emailsList || emailsList.length === 0) {
    return [];
  }
  return emailsList.split(",").map(it => it.trim());
};

const mapRestrictionMinStay = ({ minStayType, minStay }: FormType): RestrictionMinStay => {
  const restrictionMinStay = new RestrictionMinStay();

  switch (minStayType) {
    case ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT:
      const minStayDuration = new MinStayDuration();
      minStayDuration.setValue((minStay || 0) * 24);

      const restrictionValueConstant = new RestrictionValueConstant();
      restrictionValueConstant.setMinStayDuration(minStayDuration);

      const restrictionValue = new RestrictionValue();
      restrictionValue.setConstant(restrictionValueConstant);

      restrictionMinStay.setValue(restrictionValue);
      break;
    default:
      throw new Error(`minStayType [${minStayType}] not supported`);
  }

  return restrictionMinStay;
};

const mapRestrictionMaxStay = ({ maxStayType, maxStay }: FormType): RestrictionMaxStay => {
  const restrictionMaxStay = new RestrictionMaxStay();

  switch (maxStayType) {
    case ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT:
      const maxStayDuration = new MaxStayDuration();
      maxStayDuration.setValue((maxStay || 0) * 24);

      const restrictionValueConstant = new RestrictionValueConstant();
      restrictionValueConstant.setMaxStayDuration(maxStayDuration);

      const restrictionValue = new RestrictionValue();
      restrictionValue.setConstant(restrictionValueConstant);

      restrictionMaxStay.setValue(restrictionValue);
      break;
    default:
      throw new Error(`maxStayType [${maxStayType}] not supported`);
  }

  return restrictionMaxStay;
};

const mapRestrictionMinNotice = ({ minNoticeType, minNotice }: FormType): RestrictionMinNotice => {
  const restrictionMinNotice = new RestrictionMinNotice();

  switch (minNoticeType) {
    case ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT:
      const minNoticeDuration = new SimpleDuration();
      minNoticeDuration.setValue(minNotice || 0);

      const restrictionValueConstant = new RestrictionValueConstant();
      restrictionValueConstant.setSimpleDuration(minNoticeDuration);

      const restrictionValue = new RestrictionValue();
      restrictionValue.setConstant(restrictionValueConstant);

      restrictionMinNotice.setValue(restrictionValue);
      break;
    default:
      throw new Error(`minNoticeType [${minNoticeType}] not supported`);
  }

  return restrictionMinNotice;
};

const mapRestrictionMaxNotice = ({ maxNoticeType, maxNotice }: FormType): RestrictionMaxNotice => {
  const restrictionMaxNotice = new RestrictionMaxNotice();

  switch (maxNoticeType) {
    case ExtraRestrictionUse.EXTRA_RESTRICTION_USE_CONSTANT:
      const maxNoticeDuration = new SimpleDuration();
      maxNoticeDuration.setValue(maxNotice || 0);

      const restrictionValueConstant = new RestrictionValueConstant();
      restrictionValueConstant.setSimpleDuration(maxNoticeDuration);

      const restrictionValue = new RestrictionValue();
      restrictionValue.setConstant(restrictionValueConstant);

      restrictionMaxNotice.setValue(restrictionValue);
      break;
    default:
      throw new Error(`maxNoticeType [${maxNoticeType}] not supported`);
  }

  return restrictionMaxNotice;
};

const mapRestrictionCodes = (codeList: string[]): RestrictionCode => {
  const restrictionCode = new RestrictionCode();
  if (codeList.length === 0) {
    restrictionCode.setValue(undefined);
    return restrictionCode;
  }

  const unlockedCodesArray = codeList?.map((it: string) => {
    const unlockCode = new UnlockCode();
    unlockCode.setCode(it);
    return unlockCode;
  });

  const unlockCodesList = new UnlockCodes();
  unlockCodesList.setCodesList(unlockedCodesArray);

  const restrictionValueConstant = new RestrictionValueConstant();
  restrictionValueConstant.setUnlockCodes(unlockCodesList);

  const restrictionValue = new RestrictionValue();
  restrictionValue.setConstant(restrictionValueConstant);

  restrictionCode.setValue(restrictionValue);
  return restrictionCode;
};

const mapRestrictionAvailability = ({ availability }: FormType): RestrictionAvailability => {
  const restrictionAvailability = new RestrictionAvailability();

  switch (availability) {
    case ExtraUse.EXTRA_USE_OWN:
      const restrictionValue = new RestrictionValue();
      restrictionValue.setOwn(new RestrictionValueOwn());
      restrictionAvailability.setValue(restrictionValue);
      break;
    default:
      throw new Error(`availability [${availability}] not supported`);
  }

  return restrictionAvailability;
};

const mapRestrictionBlockage = ({ blockage }: FormType): RestrictionBlockage => {
  const restrictionBlockage = new RestrictionBlockage();

  switch (blockage) {
    case ExtraUse.EXTRA_USE_OWN:
      const restrictionValue = new RestrictionValue();
      restrictionValue.setOwn(new RestrictionValueOwn());
      restrictionBlockage.setValue(restrictionValue);
      break;
    default:
      throw new Error(`blockage [${blockage}] not supported`);
  }

  return restrictionBlockage;
};

const mapRestrictionTravelWindow = ({ travelWindow }: FormType): RestrictionTravelWindow => {
  // @ts-ignore
  const startDate = new proto.google.protobuf.Timestamp();
  startDate.setSeconds(moment(travelWindow.startDate).unix());
  startDate.setNanos(0);

  // @ts-ignore
  const endDate = new proto.google.protobuf.Timestamp();
  endDate.setSeconds(moment(travelWindow.endDate).unix());
  endDate.setNanos(0);

  const dateRange = new DateWindowPeriodDateRange();
  dateRange.setStartDate(startDate);
  dateRange.setEndDate(endDate);

  const period = new DateWindowPeriod();
  period.setDateRange(dateRange);

  const dateWindow = new DateWindow();
  dateWindow.setIsOpen(true);
  dateWindow.addPeriod(period);

  const restrictionValueConstant = new RestrictionValueConstant();
  restrictionValueConstant.setDateWindow(dateWindow);

  const restrictionValue = new RestrictionValue();
  restrictionValue.setConstant(restrictionValueConstant);

  const restrictionTravelWindow = new RestrictionTravelWindow();
  restrictionTravelWindow.setValue(restrictionValue);

  return restrictionTravelWindow;
};

const mapAppliesToInventoryList = ({ inventoryLinesList }: FormType): BookableRef[] => {
  const inventory = new BookableRefInventory();
  inventory.setTickersList(inventoryLinesList);

  const bookableRefList = new BookableRef();
  bookableRefList.setInventory(inventory);

  return [bookableRefList];
};

const mapMultimediaList = (multimediaList?: Image[]): Multimedia[] => {
  if (!multimediaList || multimediaList.length === 0) {
    return [];
  }

  return multimediaList
    .filter(({ url }) => !url.includes("blob:"))
    .map(({ id, filename, url, translations }: Image) => {
      const result = new Multimedia();
      result.setId(id);
      result.setFilename(filename);
      result.setUrl(url);

      if (translations) {
        result.setTranslationsList(
          Object.entries(translations).map(([locale, { title, description }]) => {
            const translation = new MultimediaTranslation();
            translation.setLocale(locale);
            translation.setTitle(title);
            translation.setDescription(description || "");
            return translation;
          })
        );
      }
      return result;
    });
};

const mapRate = ({ rateType, ratePrice }: FormType): Rate => {
  const value = new RateConstant();
  const rate = new Rate();
  value.setValue(ratePrice);

  switch (rateType) {
    case ExtraRateType.EXTRA_RATE_TYPE_CONSTANT:
      rate.setConstant(value);
      break;
    case ExtraRateType.EXTRA_RATE_TYPE_OWN:
      rate.setOwn(value);
      break;
    default:
      break;
  }

  return rate;
};

const mapAppliesToGuestsList = ({ type, applyToGuests }: FormType): GuestType[] => {
  switch (type) {
    case ExtraType.EXTRA_TYPE_PERSON:
    case ExtraType.EXTRA_TYPE_PERSON_PER_DAY:
    case ExtraType.EXTRA_TYPE_PERSON_PER_DAY_MANDATORY:
    case ExtraType.EXTRA_TYPE_PERSON_MANDATORY:
    case ExtraType.EXTRA_TYPE_PERSON_SPECIFIC_DATE:
      return applyToGuests.map(guestType => {
        switch (guestType) {
          case GuestTypeEnum.ADULT:
            return GuestType.ADULT;
          case GuestTypeEnum.CHILD:
            return GuestType.CHILD;
          case GuestTypeEnum.SENIOR:
            return GuestType.SENIOR;
          case GuestTypeEnum.TEENAGER:
            return GuestType.TEENAGER;
          case GuestTypeEnum.BABY:
            return GuestType.BABY;
          default:
            throw new Error(`guestType [${guestType}] not supported`);
        }
      });
    default:
      return [];
  }
};

const mapRestrictions = (data: FormType): Restrictions => {
  const restrictions = new Restrictions();

  if (data.availability !== ExtraUse.EXTRA_USE_UNKNOWN) {
    restrictions.setAvailability(mapRestrictionAvailability(data));
  }
  if (data.blockage !== ExtraUse.EXTRA_USE_UNKNOWN) {
    restrictions.setBlockage(mapRestrictionBlockage(data));
  }
  if (data.minStayType !== ExtraRestrictionUse.EXTRA_RESTRICTION_USE_UNKNOWN) {
    restrictions.setMinStay(mapRestrictionMinStay(data));
  }
  if (data.maxStayType !== ExtraRestrictionUse.EXTRA_RESTRICTION_USE_UNKNOWN) {
    restrictions.setMaxStay(mapRestrictionMaxStay(data));
  }
  if (data.minNoticeType !== ExtraRestrictionUse.EXTRA_RESTRICTION_USE_UNKNOWN) {
    restrictions.setMinNotice(mapRestrictionMinNotice(data));
  }
  if (data.maxNoticeType !== ExtraRestrictionUse.EXTRA_RESTRICTION_USE_UNKNOWN) {
    restrictions.setMaxNotice(mapRestrictionMaxNotice(data));
  }
  if (data.travelWindow.startDate && data.travelWindow.endDate) {
    restrictions.setTravelWindow(mapRestrictionTravelWindow(data));
  }

  restrictions.setCode(
    mapRestrictionCodes(
      data.codesList
        ?.toString()
        .split(",")
        .map(it => it.trim())
        .filter(it => it !== "")
    )
  );

  return restrictions;
};
